import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {IdleLockService, PasskeyService} from '../../_services';
import {
  isPasskeySupported, isSilentCeremonyFailure, newAbortController, whenPageReady, whenTranslationsLoaded
} from '../models/passkey.util';
import {
  LAST_HEARTBEAT_KEY, REOPEN_LOCKED_KEY, WEB_SESSION_KEY, clearReopenLocked, recordHeartbeat, safeReturnUrl,
  shouldLockNow, storeReopenGrace
} from '../models/web-session.util';
import {UserIdentity} from '../../helpers/common/user.identity';
import {environment} from '../../../environments/environment';

/**
 * JS-1396 idle lock: the device was idle past the reopen grace. Passkey only -
 * the api_token is still valid, the password is not asked again. Recovery
 * code and "set up on this device" stay reachable through Log out.
 */
@Component({
  moduleId: module.id,
  selector: 'app-passkey-unlock',
  templateUrl: `../views/passkey-unlock.component.html`,
  styleUrls: ['../styles/auth-style.scss'],
  providers: [MessageService]
})
export class PasskeyUnlockComponent implements OnInit, OnDestroy {
  public logoUrl = environment.logoUrl;
  public verifying = false;
  public noPasskey = false;
  public supported = isPasskeySupported();
  // Bleona c. 59311: nothing is painted until the start-up decision is final
  // and the account language has loaded. A new tab next to an open one is
  // locked at once and undone within TAB_PONG_WAIT_MS, so it never shows this
  // screen at all; a real reopen shows it once, already in the right language.
  public showScreen = false;
  private returnUrl = '/';
  // Automatic prompt: at most one per visit, and none once the user clicked.
  private ceremonyStarted = false;
  private destroyed = false;
  private cancelAutoStart: () => void = () => {};
  // The open ceremony: aborted when the screen is left, which closes the OS sheet.
  private ceremony: AbortController | null = null;

  // Another tab unlocked this browser (or proved it was already open): follow
  // it back. While reopenLocked is set shouldLockNow() holds, so heartbeats
  // from other tabs' api answers do not bounce this screen.
  private onStorage = (event: StorageEvent) => {
    if ((event.key === LAST_HEARTBEAT_KEY || event.key === REOPEN_LOCKED_KEY) && !shouldLockNow(Date.now())) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private passkeyService: PasskeyService,
    private messageService: MessageService,
    private translate: TranslateService,
    private idleLock: IdleLockService
  ) {}

  ngOnInit() {
    if (!UserIdentity.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }
    // Session predates the passkey rollout: there is no webSession to revive
    // and no passkey to verify against. Without this, a 423 from the api
    // bounces here, shouldLockNow() is false (hasDeviceSession needs
    // webSession), leaveUnlocked() navigates home, home's next request 423s
    // again — infinite loop while showScreen stays false (blank page).
    if (!localStorage.getItem(WEB_SESSION_KEY)) {
      this.logout();
      return;
    }
    this.returnUrl = safeReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'));
    // Nothing is locked (browser Back onto this screen after an unlock, an old
    // link): there is nothing to unlock and the session is live, so an
    // automatic prompt here would open the OS sheet for nothing. Go back.
    // During a new tab's answer window the reopen flag is set, so this holds
    // and the start-up decision rules below apply.
    if (!shouldLockNow(Date.now())) {
      this.leaveUnlocked();
      return;
    }
    window.addEventListener('storage', this.onStorage);
    this.revealWhenSettled();
  }

  private revealWhenSettled() {
    const decision = this.idleLock.decisionPending ? this.idleLock.whenDecided() : Promise.resolve(null);
    Promise.all([decision, whenTranslationsLoaded(this.translate)]).then(([ended]) => {
      if (!this.destroyed && ended !== 'undone') {
        this.showScreen = true;
        // The automatic prompt opens over the painted screen, not a blank page.
        this.scheduleAutoUnlock();
      }
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.cancelAutoStart();
    if (this.ceremony) {
      this.ceremony.abort();
    }
    window.removeEventListener('storage', this.onStorage);
  }

  /**
   * Open the passkey prompt without a click - once, when the page is visible
   * and IdleLockService's start-up decision is final. A new tab next to an
   * open one is locked at once and undone by the other tab's answer within
   * TAB_PONG_WAIT_MS: prompting in that window would flash an OS dialog in
   * every such tab, so a decision that undoes the lock never prompts. A
   * decision already final when this screen opens (an idle lock, a reload)
   * prompts straight away.
   */
  private scheduleAutoUnlock() {
    if (!this.supported) {
      return;
    }
    const schedule = () => {
      if (!this.destroyed) {
        this.cancelAutoStart = whenPageReady(() => this.autoUnlock());
      }
    };
    if (!this.idleLock.decisionPending) {
      schedule();
      return;
    }
    this.idleLock.whenDecided().then(decision => {
      if (decision !== 'undone') {
        schedule();
      }
    });
  }

  private autoUnlock() {
    if (this.destroyed || this.ceremonyStarted || this.verifying || this.noPasskey || !this.supported) {
      return;
    }
    // Unlocked meanwhile (another tab, while the decision was pending): no
    // prompt, follow it back.
    if (!shouldLockNow(Date.now())) {
      this.leaveUnlocked();
      return;
    }
    this.unlock(true);
  }

  /** `auto`: the automatic prompt (no click) - a cancel there is not toasted. */
  async unlock(auto = false) {
    if (!this.supported || this.verifying) {
      return;
    }
    this.ceremonyStarted = true;
    this.verifying = true;

    let optionsResponse: any;
    try {
      optionsResponse = await this.passkeyService.unlockOptions().toPromise();
    } catch (error) {
      this.verifying = false;
      const status = (error as HttpErrorResponse)?.status;
      if (status === 422) {
        this.noPasskey = true;
        return;
      }
      if (status === 401) {
        // monthly limit reached or signed out elsewhere: full sign-in. Also
        // after the screen was left - the session is gone either way.
        this.logout();
        return;
      }
      if (this.destroyed) {
        return;   // the screen is gone: nothing to show
      }
      this.toastFailed();
      return;
    }

    // Local ceremony: a cancelled prompt sends nothing, so just let them retry.
    // Cancelling the automatic prompt (or Safari refusing one nobody clicked
    // for) leaves the screen as it is, button and Log out, without a toast.
    // Leaving the screen (another tab unlocked, Log out) aborts it: that
    // AbortError is ours, not the user's failure - no toast. An assertion that
    // arrives after the screen is gone is not sent either.
    if (this.destroyed) {
      this.verifying = false;
      return;
    }
    let assertion: any;
    this.ceremony = newAbortController();
    try {
      assertion = await this.passkeyService.getCredential(optionsResponse.options, this.ceremony ? this.ceremony.signal : undefined);
    } catch (error) {
      this.verifying = false;
      if (!this.destroyed && !(auto && isSilentCeremonyFailure(error))) {
        this.toastFailed();
      }
      return;
    } finally {
      this.ceremony = null;
    }
    if (this.destroyed) {
      this.verifying = false;
      return;
    }

    let response: any;
    try {
      response = await this.passkeyService.unlockVerify(optionsResponse.login_token, assertion).toPromise();
    } catch (error) {
      // ErrorInterceptor already toasted the api's message.
      this.verifying = false;
      return;
    }

    // Same session row revived on the api: keep the token, restart the clock.
    // Deliberately not completePostLogin() (its language reload would lose
    // returnUrl).
    storeReopenGrace(response && response.reopen_grace);
    clearReopenLocked();
    recordHeartbeat(Date.now());
    // Left meanwhile (Log out, another tab): the storage above keeps the front
    // in step with the revived session, but the navigation is not ours to make.
    if (!this.destroyed) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }

  logout() {
    this.router.navigate(['/logout']);
  }

  /**
   * Nothing to unlock: back to the returnUrl, replacing this history entry so
   * browser Back does not land here again and bounce forward (a Back trap).
   */
  private leaveUnlocked() {
    this.router.navigateByUrl(this.returnUrl, {replaceUrl: true});
  }

  private toastFailed() {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('Error!'),
      detail: this.translate.instant('passkey verification failed')
    });
  }
}
